// Small shared UI helpers with premium touches
import { motion } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8"
    >
      <div>
        {eyebrow && <div className="eyebrow mb-2" data-testid="page-eyebrow">{eyebrow}</div>}
        <h1 className="font-display text-[32px] md:text-[40px] leading-[1.05] tracking-tight text-[hsl(var(--text-strong))]" data-testid="page-title">{title}</h1>
        {subtitle && <p className="mt-2 text-[15px] text-[hsl(var(--text-muted))] max-w-xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </motion.div>
  );
}

import PaperPlaneEmpty from "@/components/PaperPlaneEmpty";
import AnimatedNumber from "@/components/AnimatedNumber";

export function EmptyState({ title, hint, action, icon: Icon, plain = false, variant = "default", type }) {
  const { t } = useI18n();
  return (
    <div className="py-16 px-6 text-center" data-testid="empty-state">
      {plain && Icon ? (
        <div className="mx-auto w-14 h-14 rounded-2xl grid place-items-center bg-[hsl(var(--muted))] mb-5">
          <Icon size={22} className="text-[hsl(var(--text-muted))]" strokeWidth={1.75} />
        </div>
      ) : (
        <div className="mb-3 flex justify-center">
          <PaperPlaneEmpty size={140} type={type} variant={variant} />
        </div>
      )}
      <div className="font-display text-[19px] font-bold text-[hsl(var(--text-strong))]">{title || t("no_data")}</div>
      {hint && <div className="text-[14px] text-[hsl(var(--text-muted))] mt-2 max-w-sm mx-auto leading-relaxed">{hint}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function Chip({ variant = "neutral", children, ...rest }) {
  return <span className={`chip chip-${variant}`} {...rest}>{children}</span>;
}

export function statusVariant(status) {
  return {
    approved: "success", accepted: "success", published: "success",
    rejected: "danger", declined: "danger",
    pending: "warning", submitted: "warning",
    draft: "neutral", cancelled: "neutral",
    shift: "primary", leave: "warning", swap: "info", broadcast: "primary", info: "neutral", time: "info",
  }[status] || "neutral";
}

// Legacy StatCard for other pages (Dashboard has its own now)
export function StatCard({ label, value, hint, testid, accent }) {
  return (
    <div data-testid={testid} className="card p-6">
      <div className="eyebrow">{label}</div>
      <div className="font-display text-[36px] leading-none tracking-tight mt-3 tabular-nums" style={accent ? { color: accent } : undefined}>
        {value === null || value === undefined ? "–" : <AnimatedNumber value={value} />}
      </div>
      {hint && <div className="text-xs text-[hsl(var(--text-muted))] mt-2">{hint}</div>}
    </div>
  );
}
